import { IChartApi } from 'lightweight-charts';
import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createContext } from 'react';

import {
  CHART_EVENTS,
  VOL_SURFACES,
  TENORS,
  SUBSCRIPTIONS_KEY,
} from '../../constants/websocket-events';
import { CurrencyTypeEnum } from '../../model/currency_type.enum';
import { RangeEnum } from '../../model/range.enum';
import Utils from '../../utils/immersive_utils';
import usePersistentState from '../use-persistent-state';
import { WebSocketContext } from '../websocket-context/websocket-provider';
import { SeriesItem, SeriesState } from './series-reducer';
import { useChartUtilities } from './hooks/use-chart-utilities';
import { IAddSeries, useSeriesManager } from './hooks/use-series-manager';
import { PERPETUAL_ID } from './market-data.constants';
import { ChartTypeEnum } from '../../model/chart_type.enum';
import { IFetchPerpetualHistorical, usePerpetualData } from './hooks/use-perpetual-data';
import { getCurrentDate } from '../../utils/date';
import { IFetchVolsHistorical, useVolData } from './hooks/use-vols-data';
import { useSmartInactivityWake } from '../../hooks/use-smart-inactivity-wake';
import { useAppVisibility } from '../../hooks/use-app-visibility';
import { GlobalContext } from '../global-provider';

interface ISocketParamsProvider {
  children: ReactNode;
  currency?: CurrencyTypeEnum;
  providerKey?: string;
}

interface ISocketProvider {
  isLoadingPerpetualHistorical: boolean;
  setIsLoadingPerpetualHistorical: (loading: boolean) => void;
  isLoadingVolsHistorical: boolean;
  setLoadingVolsHistorical: (loading: boolean) => void;
  isFetchingCmvolSeries: boolean;
  isFetchingPerpetualSeries: boolean;
  range: RangeEnum;
  setRange: (props: RangeEnum) => void;
  chartType: ChartTypeEnum;
  setChartType: (props: ChartTypeEnum) => void;
  hasFlyChart: boolean;
  token: CurrencyTypeEnum;
  setToken: (token: CurrencyTypeEnum) => void;
  chart: React.MutableRefObject<IChartApi | undefined>;
  chartExtra: React.MutableRefObject<IChartApi | undefined>;
  chartContainerRef: React.MutableRefObject<any>;
  chartExtraContainerRef: React.MutableRefObject<any>;
  chartColorsMap: {
    colors: MutableRefObject<string[]>;
    volSeriesColorMap: MutableRefObject<Map<string, string>>;
    flySeriesColorMap: MutableRefObject<Map<string, string>>;
    futureSeriesColorMap: MutableRefObject<Map<string, string>>;
    resetColors: () => void;
  };
  series: SeriesState;
  addSeries: (props: IAddSeries) => void;
  removeSeries: (id: string) => void;
  updateSeries: (id: string, data: SeriesItem['data']) => void;
  resetAll: () => void;
  removeAllVolSeries?: () => void;
  isToStopGraphsSyncRef: MutableRefObject<boolean>;
  unsubscribe: (value: string) => void;
  fetchPerpetualHistorical: (props: IFetchPerpetualHistorical) => Promise<void> | undefined;
  fetchVolsHistorical: (props: IFetchVolsHistorical) => void;
  handleRangeChange: (newRange: RangeEnum) => Promise<void>;
  generateCompositeId: (id: string) => string;
  extractCompositeId: (id: string) => string | undefined;
}

VOL_SURFACES.forEach((volSurface) => {
  TENORS.forEach((tenor) => {
    CHART_EVENTS.push(`CMVOL_${volSurface}_${tenor}`);
    CHART_EVENTS.push(`CMVOL_${volSurface}_${tenor}_UPDATE`);
  });
});

export const MarketDataContext = createContext<ISocketProvider>({} as ISocketProvider);

export const MarketDataProvider = (props: ISocketParamsProvider) => {
  const STATE_PATH = [props.providerKey ? `TAB_${props.providerKey}` : 'MARKET_DATA'];

  const { chartColorsMap } = useChartUtilities();
  const [token, setToken] = usePersistentState<CurrencyTypeEnum>(
    STATE_PATH,
    'token',
    props.currency ?? CurrencyTypeEnum.BITCOIN,
  );
  const { marketDataOverviewCurrentTab } = useContext(GlobalContext);

  const selectedToken = useMemo<CurrencyTypeEnum>(
    () => props.currency ?? token,
    [props.currency, token],
  );

  const currency = selectedToken;

  const {
    unsubscribeFromChannel: unsubscribe,
    isConnected,
    isReconnection,
  } = useContext(WebSocketContext);

  const [hasFlyChart, setHasFlyChart] = useState(false);
  const [range, setRange] = usePersistentState<RangeEnum>(STATE_PATH, 'range', RangeEnum.D1);

  const chart = useRef<IChartApi | undefined>(undefined);
  const chartExtra = useRef<IChartApi | undefined>(undefined);
  const chartContainerRef = useRef<any>(undefined);
  const chartExtraContainerRef = useRef<any>(undefined);
  const isFirstRender = useRef<boolean>(true);

  const [chartType, setChartType] = usePersistentState<ChartTypeEnum>(
    ['CHART', currency || ''],
    'chartTypeRef',
    ChartTypeEnum.Area,
  );
  const seriesRef = useRef<SeriesState>({});
  const hasHydratedSavedQueriesRef = useRef<boolean>(false);

  const isAppVisibleRef = useAppVisibility();

  const generateCompositeId = (id: string) => {
    return Utils.generateCompositeIdWithPrefix(id, currency || '', props.providerKey);
  };

  const extractCompositeId = useCallback((id: string) => {
    const parts = id?.split('_');

    if (parts?.length > 1 && parts[parts?.length - 1] === 'USDC') {
      return `${parts[parts?.length - 2]}_${parts[parts?.length - 1]}`;
    }

    return parts?.pop();
  }, []);

  const {
    series,
    addSeries,
    removeAllCmvolSeries,
    removeSeries,
    updateSeries,
    appendSeriesTick,
    resetAll,
  } = useSeriesManager({
    chart,
    chartExtra,
    chartType,
    chartColorsMap,
    range,
    seriesRef,
    chartExtraContainerRef,
    setHasFlyChart,
  });

  const {
    fetchPerpetualHistorical,
    loading: isLoadingPerpetualHistorical,
    setLoading: setIsLoadingPerpetualHistorical,
    subscribeLive: subscribePerpetualLive,
    isFetchingPerpetualSeries,
  } = usePerpetualData(
    currency as CurrencyTypeEnum,
    props.providerKey,
    updateSeries,
    appendSeriesTick,
  );

  const {
    fetchVolsHistorical,
    subscribeLive: subscribeVolsLive,
    loading: isLoadingVolsHistorical,
    setLoading: setLoadingVolsHistorical,
    isFetchingCmvolSeries,
    isToStopGraphsSyncRef,
  } = useVolData(
    currency as CurrencyTypeEnum,
    props.providerKey,
    range,
    seriesRef,
    updateSeries,
    appendSeriesTick,
    chart,
    chartExtra,
  );

  const getCmvolSeries = (seriesParams?: SeriesState) => {
    const currentSeries = seriesParams || seriesRef.current;
    const cmvolSeries = Object.values(currentSeries).filter((s) => s.type !== 'perp');
    const queries = cmvolSeries.map((s) => {
      const [, label, tenor] = s.id.split('_');
      return { label, tenor, color: s.color } as { label: string; tenor: string; color?: string };
    });

    return queries;
  };

  const loadAndFetchSavedCmvolSeries = async () => {
    const basePath = STATE_PATH.join('/');
    const savedQueriesKey = `${basePath}/${currency}/savedQueries`;

    const migrateOldSavedQueriesIfNeeded = () => {
      try {
        if (props.providerKey) return;

        const current = localStorage.getItem(savedQueriesKey);
        if (current && current !== '[]') return;

        const byKey = new Map<string, { label: string; tenor: string; color?: string }>();

        const extractFromOldItem = (item: any) => {
          if (item && typeof item.id === 'string') {
            const parts = item.id.split('_');
            if (parts.length >= 3) {
              const label = parts[1];
              const tenor = parts[2];
              if (label && tenor)
                return {
                  label,
                  tenor,
                  color: typeof item.color === 'string' ? item.color : undefined,
                };
            }
          }

          if (item && typeof item.label === 'string') {
            const chunks = item.label.trim().split(/\s+/);
            if (chunks.length >= 2) {
              const tenor = chunks[chunks.length - 1];
              const label = chunks.slice(0, -1).join(' ');
              if (label && tenor)
                return {
                  label,
                  tenor,
                  color: typeof item.color === 'string' ? item.color : undefined,
                };
            }
          }
          return undefined;
        };

        const oldKey = `MARKET_DATA/${currency}/volSeriesSaved`;
        const raw = localStorage.getItem(oldKey);
        if (raw) {
          try {
            const arr: any[] = JSON.parse(raw) || [];
            arr.forEach((it) => {
              const parsed = extractFromOldItem(it);
              if (!parsed) return;
              const k = `${parsed.label}_${parsed.tenor}`;
              if (!byKey.has(k)) byKey.set(k, parsed);
            });
          } catch {
            // noop
          }
        }

        const migrated = Array.from(byKey.values());
        if (!migrated.length) return;

        localStorage.setItem(savedQueriesKey, JSON.stringify(migrated));
      } catch {
        // noop
      }
    };

    migrateOldSavedQueriesIfNeeded();

    const cmvolSeriesExisting = getCmvolSeries();
    const savedQueriesRaw = localStorage.getItem(savedQueriesKey);

    if (!savedQueriesRaw) return;

    try {
      const parsed: {
        label: string;
        tenor: string;
        color?: string;
      }[] = JSON.parse(savedQueriesRaw);
      if (!parsed.length) return;

      setLoadingVolsHistorical(true);

      if (cmvolSeriesExisting.length === 0) {
        await Promise.all(
          parsed.map(async (item) => {
            const rawId = `CMVOL_${item.label}_${item.tenor}_${currency}`;
            const type = rawId.includes('FLY') || rawId.includes('RR') ? 'fly' : 'vol';
            return addSeries({
              rawId,
              type,
              label: `${item.label} ${item.tenor}`,
              maintainedColor: item.color,
            });
          }),
        );
      }

      fetchVolsHistorical({
        endDate: getCurrentDate(),
        queries: parsed.map(({ label, tenor }) => ({ label, tenor })),
        newRange: range,
        options: { isRefresh: true },
      });
    } catch (err) {
      console.error('Error loading savedQueries:', err);
    } finally {
      hasHydratedSavedQueriesRef.current = true;
    }
  };

  useEffect(() => {
    const currentTabKey = marketDataOverviewCurrentTab?.toLowerCase?.() ?? '';
    const isOverview = currentTabKey.startsWith('overview_');
    const overviewMatch =
      isOverview && currentTabKey.includes(`overview_${currency.toLowerCase()}`);

    if (!props.providerKey && !overviewMatch) return;

    if (isFirstRender.current) {
      setIsLoadingPerpetualHistorical(true);
      isFirstRender.current = false;
    }

    if (!isAppVisibleRef.current || !isConnected || isReconnection) return;

    (async () => {
      const isPerpExist = Object.keys(series).includes(PERPETUAL_ID);
      const currentLiveId = `${SUBSCRIPTIONS_KEY.CANDLE_SUBSCRIPTION}_${currency}`;
      unsubscribe(currentLiveId);

      if (isPerpExist) {
        if (!isFirstRender.current) {
          setIsLoadingPerpetualHistorical(true);
        }

        await fetchPerpetualHistorical({
          endDate: getCurrentDate(),
          range,
        });
      } else {
        await addSeries({
          rawId: PERPETUAL_ID,
          type: 'perp',
          label: 'Perpetual',
          perpetualCallback: async () =>
            await fetchPerpetualHistorical({
              endDate: getCurrentDate(),
              range,
            }),
        });
      }

      subscribePerpetualLive();
      unsubscribe(generateCompositeId('CMVOL_SUBSCRIPTION'));
      loadAndFetchSavedCmvolSeries();
      subscribeVolsLive();
    })();
  }, [isConnected, currency, isReconnection, marketDataOverviewCurrentTab]);

  useEffect(() => {
    const currentTabKey = marketDataOverviewCurrentTab?.toLowerCase?.() ?? '';
    const isOverview = currentTabKey.startsWith('overview_');
    const customMatch = !isOverview && currentTabKey.includes(`custom_tab_`);

    if (!props.providerKey) return;
    if (!customMatch) return;

    if (isFirstRender.current) {
      setIsLoadingPerpetualHistorical(true);
      isFirstRender.current = false;
    }

    if (!isAppVisibleRef.current || !isConnected || isReconnection) return;

    (async () => {
      const isPerpExist = Object.keys(series).includes(PERPETUAL_ID);
      const currentLiveId = `${SUBSCRIPTIONS_KEY.CANDLE_SUBSCRIPTION}_${currency}${props.providerKey ? `-${props.providerKey}` : ''}`;
      unsubscribe(currentLiveId);

      if (isPerpExist) {
        if (!isFirstRender.current) {
          setIsLoadingPerpetualHistorical(true);
        }

        await fetchPerpetualHistorical({
          endDate: getCurrentDate(),
          range,
        });
      } else {
        await addSeries({
          rawId: PERPETUAL_ID,
          type: 'perp',
          label: 'Perpetual',
          perpetualCallback: async () =>
            await fetchPerpetualHistorical({
              endDate: getCurrentDate(),
              range,
            }),
        });
      }

      subscribePerpetualLive();
      unsubscribe(generateCompositeId('CMVOL_SUBSCRIPTION'));
      loadAndFetchSavedCmvolSeries();
      subscribeVolsLive();
    })();
  }, [isConnected, currency, isReconnection, marketDataOverviewCurrentTab, props.providerKey]);

  useEffect(() => {
    if (!isConnected) return;

    seriesRef.current = series;

    const queries = getCmvolSeries(seriesRef.current);
    const serialized = JSON.stringify(queries);
    if (!hasHydratedSavedQueriesRef.current && queries.length === 0) {
      return;
    }

    const basePath = STATE_PATH.join('/');
    const savedQueriesKey = `${basePath}/${currency}/savedQueries`;
    localStorage.setItem(savedQueriesKey, serialized);

    if (props.providerKey) {
      const TOKENS_TO_SYNC: CurrencyTypeEnum[] = [
        CurrencyTypeEnum.BITCOIN,
        CurrencyTypeEnum.ETHEREUM,
        CurrencyTypeEnum.SOL,
        CurrencyTypeEnum.XRP,
      ];

      TOKENS_TO_SYNC.forEach((tkn) => {
        const path = [`TAB_${props.providerKey}`, tkn].join('/');
        localStorage.setItem(`${path}/savedQueries`, serialized);
      });
    }
  }, [series]);

  const handleRangeChange = useCallback(
    async (newRange: RangeEnum) => {
      if (!isAppVisibleRef.current || !isConnected || isReconnection) return;

      const liveId = `${SUBSCRIPTIONS_KEY.CANDLE_SUBSCRIPTION}_${currency}${props.providerKey ? `-${props.providerKey}` : ''}`;
      unsubscribe(liveId);
      subscribePerpetualLive();

      setIsLoadingPerpetualHistorical(true);
      await fetchPerpetualHistorical({
        endDate: getCurrentDate(),
        range: newRange,
      });

      const cmvolSeries = getCmvolSeries();
      if (cmvolSeries.length > 0) {
        setLoadingVolsHistorical(true);
        unsubscribe(generateCompositeId('CMVOL_SUBSCRIPTION'));
        subscribeVolsLive();
        fetchVolsHistorical({
          endDate: getCurrentDate(),
          queries: cmvolSeries,
          newRange,
          options: { isRefresh: true },
        });
      }
    },
    [
      isConnected,
      range,
      series,
      fetchPerpetualHistorical,
      fetchVolsHistorical,
      setIsLoadingPerpetualHistorical,
      isLoadingPerpetualHistorical,
      setLoadingVolsHistorical,
      isLoadingVolsHistorical,
    ],
  );

  useEffect(() => {
    if (!isConnected) return;

    const currentPerpetual = seriesRef.current[PERPETUAL_ID];
    if (!currentPerpetual || currentPerpetual.chartType === chartType) return;
    const previousCmvolSeries = Object.values(seriesRef.current)
      .filter((s) => s.type !== 'perp')
      .map((s) => ({ id: s.id, label: s.label, type: s.type, color: s.color }));

    removeAllCmvolSeries();
    setIsLoadingPerpetualHistorical(true);
    addSeries({
      rawId: PERPETUAL_ID,
      type: 'perp',
      label: 'Perpetual',
      force: true,
      perpetualCallback: async () => {
        await fetchPerpetualHistorical({
          endDate: getCurrentDate(),
          range,
        });

        if (previousCmvolSeries.length) {
          setLoadingVolsHistorical(true);
          const queries: { label: string; tenor: string }[] = [];
          for (const prev of previousCmvolSeries) {
            const { id, label, type, color } = prev;
            const parts = id.split('_');
            if (parts.length < 4) continue;
            const surface = parts[1];
            const tenor = parts[2];
            queries.push({ label: surface, tenor });
            await addSeries({ rawId: id, type, label, force: true, maintainedColor: color });
          }
          if (queries.length) {
            fetchVolsHistorical({
              endDate: getCurrentDate(),
              queries,
              newRange: range,
              options: { isRefresh: true },
            });
          }
        }
      },
    });
  }, [chartType]);

  useSmartInactivityWake(() => {
    if (isLoadingPerpetualHistorical || isLoadingVolsHistorical) return;
    const fetchDelay = 3000; // 3s

    setTimeout(() => handleRangeChange(range), fetchDelay);
  });

  return (
    <MarketDataContext.Provider
      value={{
        chart,
        chartExtra,
        chartType,
        setChartType,
        chartContainerRef,
        chartExtraContainerRef,
        chartColorsMap,
        isLoadingPerpetualHistorical,
        isLoadingVolsHistorical,
        setLoadingVolsHistorical,
        setIsLoadingPerpetualHistorical,
        isFetchingCmvolSeries,
        isFetchingPerpetualSeries,
        isToStopGraphsSyncRef,
        range,
        setRange,
        hasFlyChart,
        unsubscribe,
        fetchPerpetualHistorical,
        fetchVolsHistorical,
        handleRangeChange,
        addSeries,
        removeSeries,
        removeAllVolSeries: removeAllCmvolSeries,
        updateSeries,
        resetAll,
        series,
        generateCompositeId,
        extractCompositeId,
        token: selectedToken,
        setToken: (t: CurrencyTypeEnum) => {
          if (props.currency == null) {
            setToken(t);
          }
        },
      }}
    >
      {props.children}
    </MarketDataContext.Provider>
  );
};
